import { Minus, Plus, RotateCcw } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type GestureResponderEvent,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
  useWindowDimensions,
} from 'react-native';

import { LoadableImage } from '@/src/components/LoadableImage';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import {
  clampPhotoPanForLayout,
  clampPhotoZoom,
  isDesktopPinchWheel,
  MAX_PHOTO_ZOOM,
  MIN_PHOTO_ZOOM,
  nextPhotoZoomFromWheel,
  PHOTO_ZOOM_STEP,
} from './zoomable-photo-gesture';

const DOUBLE_TAP_MS = 280;
const MIN_PAN_DISTANCE = 4;

type ZoomablePhotoProps = {
  accessibilityLabel: string;
  captureDesktopPinch?: boolean;
  loaderColor?: string;
  onZoomChange?: (zoomed: boolean) => void;
  sourceURL: string;
  style?: StyleProp<ViewStyle>;
};

type TouchPoint = {
  pageX: number;
  pageY: number;
};

type GestureState =
  | {
      initialCenter: TouchPoint;
      initialDistance: number;
      initialPan: { x: number; y: number };
      initialZoom: number;
      mode: 'pinch';
    }
  | {
      initialPan: { x: number; y: number };
      initialTouch: TouchPoint;
      mode: 'pan';
    };

type WebGestureEvent = Event & {
  scale?: number;
};

function distanceBetween(first: TouchPoint, second: TouchPoint) {
  return Math.hypot(first.pageX - second.pageX, first.pageY - second.pageY);
}

function centerBetween(first: TouchPoint, second: TouchPoint) {
  return {
    pageX: (first.pageX + second.pageX) / 2,
    pageY: (first.pageY + second.pageY) / 2,
  };
}

function touchesFromEvent(event: GestureResponderEvent): TouchPoint[] {
  const nativeEvent = event.nativeEvent as unknown as {
    pageX?: number;
    pageY?: number;
    touches?: TouchPoint[];
  };

  if (nativeEvent.touches && nativeEvent.touches.length > 0) {
    return nativeEvent.touches;
  }

  if (
    typeof nativeEvent.pageX === 'number' &&
    typeof nativeEvent.pageY === 'number'
  ) {
    return [{ pageX: nativeEvent.pageX, pageY: nativeEvent.pageY }];
  }

  return [];
}

export function ZoomablePhoto({
  accessibilityLabel,
  captureDesktopPinch = true,
  loaderColor = colors.surface,
  onZoomChange,
  sourceURL,
  style,
}: ZoomablePhotoProps) {
  const { width: windowWidth } = useWindowDimensions();
  const [layout, setLayout] = useState({ height: 0, width: 0 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_PHOTO_ZOOM);
  const gestureStateRef = useRef<GestureState | null>(null);
  const gestureMovedRef = useRef(false);
  const layoutRef = useRef(layout);
  const lastTapRef = useRef(0);
  const onZoomChangeRef = useRef(onZoomChange);
  const panRef = useRef(pan);
  const pendingFrameRef = useRef<number | null>(null);
  const pendingTransformRef = useRef<{
    pan: { x: number; y: number };
    zoom: number;
  } | null>(null);
  const safariGestureStartZoomRef = useRef(MIN_PHOTO_ZOOM);
  const zoomRef = useRef(zoom);
  const zoomedRef = useRef(false);
  const zoomed = zoom > MIN_PHOTO_ZOOM;

  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  }, [onZoomChange]);

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    zoomRef.current = zoom;
    zoomedRef.current = zoom > MIN_PHOTO_ZOOM;
  }, [zoom]);

  useEffect(
    () => () => {
      if (pendingFrameRef.current != null) {
        cancelAnimationFrame(pendingFrameRef.current);
      }
    },
    [],
  );

  function handleLayout(event: LayoutChangeEvent) {
    setLayout({
      height: event.nativeEvent.layout.height,
      width: event.nativeEvent.layout.width,
    });
  }

  const applyTransform = useCallback(
    (
      nextZoom: number,
      nextPan: { x: number; y: number },
      options: { immediate?: boolean } = {},
    ) => {
      const clampedZoom = clampPhotoZoom(nextZoom);
      const clampedPan = clampPhotoPanForLayout(
        nextPan,
        clampedZoom,
        layoutRef.current,
      );

      zoomRef.current = clampedZoom;
      panRef.current = clampedPan;

      const nextZoomed = clampedZoom > MIN_PHOTO_ZOOM;

      if (zoomedRef.current !== nextZoomed) {
        zoomedRef.current = nextZoomed;
        onZoomChangeRef.current?.(nextZoomed);
      }

      if (options.immediate) {
        if (pendingFrameRef.current != null) {
          cancelAnimationFrame(pendingFrameRef.current);
          pendingFrameRef.current = null;
        }
        pendingTransformRef.current = null;
        setZoom(clampedZoom);
        setPan(clampedPan);
        return;
      }

      pendingTransformRef.current = {
        pan: clampedPan,
        zoom: clampedZoom,
      };

      if (pendingFrameRef.current != null) {
        return;
      }

      pendingFrameRef.current = requestAnimationFrame(() => {
        pendingFrameRef.current = null;

        const nextTransform = pendingTransformRef.current;
        pendingTransformRef.current = null;

        if (!nextTransform) {
          return;
        }

        setZoom(nextTransform.zoom);
        setPan(nextTransform.pan);
      });
    },
    [],
  );

  const setNextZoom = useCallback(
    (nextZoom: number) => {
      const next = clampPhotoZoom(nextZoom);
      const nextPan =
        next <= MIN_PHOTO_ZOOM
          ? { x: 0, y: 0 }
          : clampPhotoPanForLayout(panRef.current, next, layoutRef.current);

      applyTransform(next, nextPan);
    },
    [applyTransform],
  );

  useEffect(() => {
    applyTransform(MIN_PHOTO_ZOOM, { x: 0, y: 0 }, { immediate: true });
  }, [applyTransform, sourceURL]);

  function clampPan(nextPan: { x: number; y: number }, nextZoom = zoom) {
    return clampPhotoPanForLayout(nextPan, nextZoom, layoutRef.current);
  }

  function toggleDoubleTapZoom() {
    const now = Date.now();

    if (now - lastTapRef.current <= DOUBLE_TAP_MS) {
      setNextZoom(zoomed ? MIN_PHOTO_ZOOM : 2);
    }

    lastTapRef.current = now;
  }

  function shouldCaptureGesture(event: GestureResponderEvent) {
    const touches = touchesFromEvent(event);

    return touches.length >= 2 || zoomed;
  }

  function startGesture(event: GestureResponderEvent) {
    const touches = touchesFromEvent(event);
    gestureMovedRef.current = false;

    if (touches.length >= 2) {
      gestureStateRef.current = {
        initialCenter: centerBetween(touches[0], touches[1]),
        initialDistance: Math.max(1, distanceBetween(touches[0], touches[1])),
        initialPan: panRef.current,
        initialZoom: zoomRef.current,
        mode: 'pinch',
      };
      return;
    }

    if (touches[0]) {
      gestureStateRef.current = {
        initialPan: panRef.current,
        initialTouch: touches[0],
        mode: 'pan',
      };
    }
  }

  function moveGesture(event: GestureResponderEvent) {
    const touches = touchesFromEvent(event);
    const gestureState = gestureStateRef.current;

    if (!gestureState) {
      return;
    }

    if (gestureState.mode === 'pinch' && touches.length >= 2) {
      const nextCenter = centerBetween(touches[0], touches[1]);
      const nextDistance = distanceBetween(touches[0], touches[1]);
      const nextZoom = clampPhotoZoom(
        gestureState.initialZoom *
          (nextDistance / Math.max(1, gestureState.initialDistance)),
      );
      const centerDelta = {
        x: nextCenter.pageX - gestureState.initialCenter.pageX,
        y: nextCenter.pageY - gestureState.initialCenter.pageY,
      };

      gestureMovedRef.current = true;
      applyTransform(
        nextZoom,
        clampPan(
          {
            x: gestureState.initialPan.x + centerDelta.x,
            y: gestureState.initialPan.y + centerDelta.y,
          },
          nextZoom,
        ),
      );
      return;
    }

    if (gestureState.mode === 'pan' && touches[0] && zoomed) {
      const delta = {
        x: touches[0].pageX - gestureState.initialTouch.pageX,
        y: touches[0].pageY - gestureState.initialTouch.pageY,
      };

      if (
        Math.abs(delta.x) > MIN_PAN_DISTANCE ||
        Math.abs(delta.y) > MIN_PAN_DISTANCE
      ) {
        gestureMovedRef.current = true;
      }

      applyTransform(
        zoomRef.current,
        clampPan({
          x: gestureState.initialPan.x + delta.x,
          y: gestureState.initialPan.y + delta.y,
        }),
      );
    }
  }

  function endGesture() {
    gestureStateRef.current = null;

    if (!gestureMovedRef.current) {
      toggleDoubleTapZoom();
    }
  }

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !captureDesktopPinch ||
      typeof window === 'undefined'
    ) {
      return undefined;
    }

    function preventBrowserZoom(event: Event) {
      event.preventDefault();
      event.stopPropagation();
    }

    function handleWheel(event: WheelEvent) {
      if (
        !isDesktopPinchWheel({
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
        })
      ) {
        return;
      }

      preventBrowserZoom(event);

      setNextZoom(
        nextPhotoZoomFromWheel({
          currentZoom: zoomRef.current,
          deltaY: event.deltaY,
        }),
      );
    }

    function handleGestureStart(event: WebGestureEvent) {
      preventBrowserZoom(event);
      safariGestureStartZoomRef.current = zoomRef.current;
    }

    function handleGestureChange(event: WebGestureEvent) {
      preventBrowserZoom(event);
      setNextZoom(
        safariGestureStartZoomRef.current *
          (typeof event.scale === 'number' ? event.scale : 1),
      );
    }

    window.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    });
    window.addEventListener('gesturestart', handleGestureStart, {
      capture: true,
      passive: false,
    });
    window.addEventListener('gesturechange', handleGestureChange, {
      capture: true,
      passive: false,
    });
    window.addEventListener('gestureend', preventBrowserZoom, {
      capture: true,
      passive: false,
    });

    return () => {
      window.removeEventListener('wheel', handleWheel, { capture: true });
      window.removeEventListener('gesturestart', handleGestureStart, {
        capture: true,
      });
      window.removeEventListener('gesturechange', handleGestureChange, {
        capture: true,
      });
      window.removeEventListener('gestureend', preventBrowserZoom, {
        capture: true,
      });
    };
  }, [captureDesktopPinch, setNextZoom]);

  const containedTouchStyle =
    Platform.OS === 'web'
      ? ({
          touchAction: zoomed ? 'none' : 'pan-x pan-y',
          userSelect: 'none',
        } as ViewStyle)
      : null;
  const controlsPositionStyle =
    windowWidth < 560
      ? styles.controlsMobile
      : styles.controlsDesktop;

  return (
    <View
      onLayout={handleLayout}
      onMoveShouldSetResponder={shouldCaptureGesture}
      onResponderGrant={startGesture}
      onResponderMove={moveGesture}
      onResponderRelease={endGesture}
      onResponderTerminate={() => {
        gestureStateRef.current = null;
      }}
      onResponderTerminationRequest={() => !zoomed}
      onStartShouldSetResponder={shouldCaptureGesture}
      style={[styles.root, containedTouchStyle, style]}
    >
      <View
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="imagebutton"
        style={styles.imageViewport}
      >
        <View
          style={[
            styles.imageFrame,
            {
              transform: [
                { translateX: pan.x },
                { translateY: pan.y },
                { scale: zoom },
              ],
            },
          ]}
        >
          <LoadableImage
            accessibilityLabel={accessibilityLabel}
            cachePolicy="memory-disk"
            contentFit="contain"
            loaderColor={loaderColor}
            source={{ uri: sourceURL }}
            style={styles.image}
            transition={120}
          />
        </View>
      </View>
      <View
        pointerEvents="box-none"
        style={[styles.controls, controlsPositionStyle]}
      >
        <Pressable
          accessibilityLabel="Zoom out"
          accessibilityRole="button"
          disabled={zoom <= MIN_PHOTO_ZOOM}
          onPress={() => setNextZoom(zoom - PHOTO_ZOOM_STEP)}
          style={({ pressed }) => [
            styles.controlButton,
            zoom <= MIN_PHOTO_ZOOM ? styles.controlButtonDisabled : null,
            pressed && zoom > MIN_PHOTO_ZOOM
              ? styles.controlButtonPressed
              : null,
          ]}
        >
          <Minus color={colors.surface} size={18} strokeWidth={2.6} />
        </Pressable>
        <Pressable
          accessibilityLabel="Reset zoom"
          accessibilityRole="button"
          disabled={zoom <= MIN_PHOTO_ZOOM}
          onPress={() => setNextZoom(MIN_PHOTO_ZOOM)}
          style={({ pressed }) => [
            styles.controlButton,
            zoom <= MIN_PHOTO_ZOOM ? styles.controlButtonDisabled : null,
            pressed && zoom > MIN_PHOTO_ZOOM
              ? styles.controlButtonPressed
              : null,
          ]}
        >
          <RotateCcw color={colors.surface} size={17} strokeWidth={2.6} />
        </Pressable>
        <Pressable
          accessibilityLabel="Zoom in"
          accessibilityRole="button"
          disabled={zoom >= MAX_PHOTO_ZOOM}
          onPress={() => setNextZoom(zoom + PHOTO_ZOOM_STEP)}
          style={({ pressed }) => [
            styles.controlButton,
            zoom >= MAX_PHOTO_ZOOM ? styles.controlButtonDisabled : null,
            pressed && zoom < MAX_PHOTO_ZOOM
              ? styles.controlButtonPressed
              : null,
          ]}
        >
          <Plus color={colors.surface} size={18} strokeWidth={2.6} />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  controlButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderColor: 'rgba(255,255,255,0.18)',
    borderRadius: 999,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  controlButtonDisabled: {
    opacity: 0.34,
  },
  controlButtonPressed: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  controls: {
    elevation: 4,
    flexDirection: 'row',
    gap: spacing.xs,
    position: 'absolute',
    zIndex: 4,
  },
  controlsDesktop: {
    bottom: spacing.lg,
    right: spacing.lg,
  },
  controlsMobile: {
    bottom: 150,
    right: spacing.lg,
  },
  image: {
    height: '100%',
    width: '100%',
  },
  imageFrame: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  imageViewport: {
    height: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  root: {
    overflow: 'hidden',
  },
});
